import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, Users, MapPin, Play, Plus, CheckCircle, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useEvents } from '../hooks/useEvents';
import { hasScoringCardSync } from '../../scoring/scoringComponents';
import {
  hasEventStarted as checkEventStarted,
  hasEventEnded
} from '../../utils/dateTimeUtils';
import { btn } from '../../utils/buttonStyles';

/**
 * EventInfo Component - Fully Self-Contained
 *
 * Fetches and displays event information for a given owner (group/tournament/user).
 * Handles event creation and returns event VO to caller.
 *
 * Props:
 * - ownerType: 'group' | 'tournament' | 'user' (required)
 * - ownerId: ID of the owner (required)
 * - sportName: Name of the sport (optional)
 * - members: Array of members for participant count (optional)
 * - voProvider: Provider instance to get additional data (optional)
 * - onEventLoaded: Callback(event, refreshFn) - Called when event is loaded/created
 * - onStartScoring: Callback when Start Scoring button is clicked
 * - isMember: Boolean indicating if current user is a member
 */
const EventInfo = ({
  ownerType,
  ownerId,
  sportName,
  sportTypeId,
  members = [],
  voProvider,
  onEventLoaded,
  onStartScoring,
  onCompleteEvent,
  hasCompletedGame = true,
  isMember = false
}) => {
  const { theme } = useTheme();
  const {
    events: hookEvents,
    loading: hookLoading,
    fetchEvents,
    createEvent: createEventAPI
  } = useEvents(ownerType, ownerId, true); // true = auto-load events

  // Track the last event ID we notified the parent about, to avoid redundant calls
  const lastNotifiedEventIdRef = useRef(undefined); // undefined = never notified

  // Local state
  const [currentEvent, setCurrentEvent] = useState(null);
  const [eventCountdown, setEventCountdown] = useState(null);
  const [creating, setCreating] = useState(false);

  // Find next upcoming event
  const getNextUpcomingEvent = useCallback(() => {

    if (!hookEvents || hookEvents.length === 0) {
      return null;
    }

    const now = new Date();

    const relevantEvents = hookEvents
      .filter(event => {

        if (event.status === 'completed') {
          return false;
        }

        // Any non-completed event (open, in_progress, etc.) is always shown
        // regardless of end_time — the end_time is just a schedule estimate
        // and a game can run past it
        return true;
      })
      .sort((a, b) => {
        // Sort by start time
        return new Date(a.start_time) - new Date(b.start_time);
      });

    const nextEvent = relevantEvents.length > 0 ? relevantEvents[0] : null;

    return nextEvent;
  }, [hookEvents]);

  // When provider has a specific targeted event (e.g. navigated from My Games),
  // use it directly instead of computing the next upcoming event from hookEvents.
  // This handles both the initial mount case and async updates via handleUrlChange.
  useEffect(() => {
    if (!voProvider) return;

    // Check if provider already has a targeted event set (direct navigation case)
    // Ignore completed events — on the Current Event tab the user should see
    // "No Upcoming Events" / create-new flow, not a completed event.
    const providerEvent = voProvider.vo?.currentEvent?.eventData;
    if (providerEvent && providerEvent.status !== 'completed') {
      setCurrentEvent(providerEvent);
      if (onEventLoaded && providerEvent.id !== lastNotifiedEventIdRef.current) {
        lastNotifiedEventIdRef.current = providerEvent.id;
        onEventLoaded(providerEvent, fetchEvents);
      }
    }

    // Subscribe for future provider currentEvent changes (same-squad My Games navigation)
    const removeListener = voProvider.addStateChangeListener((changeType, newValue) => {
      if (changeType === 'currentEvent' && newValue?.id && newValue?.status !== 'completed') {
        setCurrentEvent(newValue);
        if (onEventLoaded && newValue.id !== lastNotifiedEventIdRef.current) {
          lastNotifiedEventIdRef.current = newValue.id;
          onEventLoaded(newValue, fetchEvents);
        }
      }
    });

    return removeListener;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voProvider]);

  // Update current event when hookEvents change.
  // Skip if provider is targeting a specific non-completed event (from My Games URL navigation) —
  // in that case the provider's event takes precedence over the auto-computed upcoming one.
  // If the targeted event is completed, fall through so getNextUpcomingEvent() runs and
  // the user sees the create-new-event flow instead.
  // We only notify the parent (onEventLoaded) when the event ID actually changes —
  // this prevents loops caused by intermediate empty-events states (e.g. React
  // StrictMode re-running useEvents, or the hook briefly setting events to []).
  useEffect(() => {
    const providerEventCompleted = voProvider?.vo?.currentEvent?.eventData?.status === 'completed';
    if (voProvider?.vo?.urlParams?.eventId && !providerEventCompleted) {
      return;
    }

    const nextEvent = getNextUpcomingEvent();
    setCurrentEvent(nextEvent);

    const nextEventId = nextEvent?.id ?? null;
    if (onEventLoaded && nextEventId !== lastNotifiedEventIdRef.current) {
      lastNotifiedEventIdRef.current = nextEventId;
      onEventLoaded(nextEvent, fetchEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookEvents]);

  // Check if event has started (use utility function)
  const hasEventStarted = useCallback((event) => {
    return checkEventStarted(event);
  }, []);

  // Update countdown timer
  useEffect(() => {
    if (!currentEvent) return;

    const updateCountdown = () => {
      const eventStartTime = new Date(currentEvent.start_time);
      const now = new Date();
      const diff = eventStartTime - now;

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setEventCountdown({ days, hours, minutes, seconds });
      } else {
        setEventCountdown(null);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [currentEvent]);

  // Handle quick create event
  const handleQuickCreateEvent = async () => {
    setCreating(true);
    try {
      const now = new Date();
      const startTime = now; // Start immediately (now)
      const endTime = new Date(startTime.getTime() + 2 * 60 * 60 * 1000); // 2 hours duration

      // Create event with timestamps
      const eventData = {
        subject: 'Quick Game',
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString()
      };

      const result = await createEventAPI(eventData);

      if (result.success) {
        // The hook automatically adds the new event to state, so just set it as current
        setCurrentEvent(result.data);
        if (onEventLoaded) {
          onEventLoaded(result.data, fetchEvents);
        }
      }
    } catch (error) {
      console.error('[EventInfo] Error creating event:', error);
    } finally {
      setCreating(false);
    }
  };

  // Get participating members
  const getParticipatingMembers = useCallback((event) => {
    if (!event || !members) return [];
    // An invitation nobody has answered is not an answer: with no answers at
    // all, everybody is taken as playing, as before invitations existed.
    const answered = (event.responses || []).filter(r => r.status !== 'invited');
    if (answered.length === 0) return members;

    const participatingIds = answered
      .filter(r => r.status === 'available' || r.status === 'join-late')
      .map(r => String(r.userId));

    return members.filter(m => participatingIds.includes(String(m.id)));
  }, [members]);

  // Loading state
  if (hookLoading) {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  // Empty state when no event
  if (!currentEvent) {
    return (
      <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
        <div className="text-center py-12">
          <Calendar className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
          <p className={`text-lg ${theme.text} mb-2`}>No Upcoming Events</p>
          <p className={`${theme.textMuted} mb-4`}>
            Create an event in the Schedule tab to get started
          </p>
          {isMember && (
            <button
              onClick={handleQuickCreateEvent}
              disabled={creating}
              className={`inline-flex items-center gap-x-2 ${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>Create Event Now (Ends in 2 Hours)</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Event display
  const eventHasStarted = hasEventStarted(currentEvent);
  const participants = getParticipatingMembers(currentEvent);
  const hasScoringAvailable = hasScoringCardSync(sportTypeId);

  return (
    <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-x-3">
          <h3 className={`text-lg font-semibold ${theme.text}`}>
            Next Upcoming Event {currentEvent && <span className="text-sm text-gray-500">(eventId: {currentEvent.id})</span>}
          </h3>
        </div>
        {isMember && !eventHasStarted && (
          <button
            onClick={handleQuickCreateEvent}
            disabled={creating}
            className={`flex items-center gap-x-2 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
          >
            {creating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating...</span>
              </>
            ) : (
              <span>+ Create Event Now</span>
            )}
          </button>
        )}
      </div>

      {/* Event Details Card */}
      <div className={`${theme.backgroundSecondary} rounded-xl overflow-hidden`}>
        {/* Status Banner */}
        <div className={`px-6 py-3 ${
          eventHasStarted
            ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
            : 'bg-gradient-to-r from-purple-500 to-blue-500'
        } text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              {eventHasStarted ? (
                <>
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="font-semibold">Event In Progress</span>
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">Upcoming Event</span>
                </>
              )}
            </div>
            {!eventHasStarted && eventCountdown && (
              <div className="text-right">
                <div className="text-xs opacity-90">Starts in</div>
                <div className="font-bold">
                  {eventCountdown.days > 0 && `${eventCountdown.days}d `}
                  {eventCountdown.hours}h {eventCountdown.minutes}m
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Content */}
        <div className="p-6">
          <div className="mb-5">
            <h4 className={`text-xl font-bold ${theme.text} mb-3`}>
              {currentEvent.subject}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className={`flex items-center gap-x-3 p-3 rounded-lg ${theme.background}`}>
                <Calendar className={`w-5 h-5 ${theme.textSecondary}`} />
                <div>
                  <div className={`text-xs ${theme.textMuted}`}>Date & Time</div>
                  <div className={`text-sm font-medium ${theme.text}`}>
                    {currentEvent.start_datetime_display}
                  </div>
                </div>
              </div>
              <div className={`flex items-center gap-x-3 p-3 rounded-lg ${theme.background}`}>
                <Users className={`w-5 h-5 ${theme.textSecondary}`} />
                <div>
                  <div className={`text-xs ${theme.textMuted}`}>Participants</div>
                  <div className={`text-sm font-medium ${theme.text}`}>
                    {participants.length} members
                  </div>
                </div>
              </div>
              {(currentEvent.venue?.name || currentEvent.venue?.fullAddress) && (
                <div className={`flex items-center gap-x-3 p-3 rounded-lg ${theme.background}`}>
                  <MapPin className={`w-5 h-5 ${theme.textSecondary}`} />
                  <div>
                    <div className={`text-xs ${theme.textMuted}`}>Location</div>
                    {currentEvent.venue?.name && (
                      <div className={`text-sm font-medium ${theme.text}`}>{currentEvent.venue.name}</div>
                    )}
                    {currentEvent.venue?.fullAddress && (
                      <div className={`text-xs ${theme.textMuted}`}>{currentEvent.venue.fullAddress}</div>
                    )}
                  </div>
                </div>
              )}
              {sportName && (
                <div className={`flex items-center gap-x-3 p-3 rounded-lg ${theme.background}`}>
                  <span className="text-lg">🏆</span>
                  <div>
                    <div className={`text-xs ${theme.textMuted}`}>Sport</div>
                    <div className={`text-sm font-medium ${theme.text}`}>{sportName}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {hasScoringAvailable && onStartScoring && eventHasStarted && (
            <div className={`pt-5 border-t ${theme.border} space-y-2`}>
              {hasCompletedGame ? (
                <>
                  <button
                    onClick={onStartScoring}
                    className={`w-full flex items-center justify-center gap-x-2 ${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Start Another Game</span>
                  </button>
                  {onCompleteEvent && (
                    <button
                      onClick={onCompleteEvent}
                      className={`w-full flex items-center justify-center gap-x-2 ${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
                    >
                      <CheckCircle className="w-5 h-5" />
                      <span>Complete Event</span>
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={onStartScoring}
                  className={`w-full flex items-center justify-center gap-x-2 ${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
                >
                  <Play className="w-5 h-5" />
                  <span>Start Scoring</span>
                </button>
              )}
            </div>
          )}

          {!hasScoringAvailable && (
            <div className={`pt-5 border-t ${theme.border}`}>
              <div className={`flex items-center justify-center px-6 py-4 ${theme.backgroundSecondary} ${theme.textSecondary} rounded-lg`}>
                <div className="text-center">
                  <div className="font-medium">Scoring Unavailable</div>
                  <div className="text-sm">Coming Soon for {sportName || 'this sport'}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventInfo;
